/* eslint-disable operator-linebreak */
/* eslint-disable implicit-arrow-linebreak */
import { IContact, IDependent, IEmployeeAuditable, IPayStatement } from 'src/interfaces';
import { IEmployee, IPermission } from 'src/interfaces/employee';
import { EmployeesActionType } from '../action-types';
import { EmployeesAction } from '../actions';

interface EmployeeState {
  loading: boolean;
  error: string | null;
  data: {
    employees: IEmployee[];
    employeesStatus?: boolean;
    totalEmployees: number;
    selectedEmployee: {
      employee?: IEmployee;
      dependents?: IDependent[];
      totalDependents?: number;
      contacts?: IContact[];
      totalContacts?: number;
      auditable?: IEmployeeAuditable[];
      loadingAuditable?: boolean;
      totalAuditable?: number;
      payStatements?: IPayStatement[];
      totalPayStatements?: number;
      companyId?: string;
    };
  };
}

const initialState = {
  loading: false,
  error: null,
  data: {
    employees: [],
    employeesStatus: true,
    totalEmployees: 0,
    selectedEmployee: {},
  },
};

const reducer = (state: EmployeeState = initialState, action: EmployeesAction): EmployeeState => {
  switch (action.type) {
    case EmployeesActionType.GET_EMPLOYEES:
      return {
        loading: true,
        error: null,
        data: { employees: [], totalEmployees: 0, selectedEmployee: {} },
      };
    case EmployeesActionType.GET_EMPLOYEES_SUCCESS:
      return {
        loading: false,
        error: null,
        data: {
          ...action.payload,
          selectedEmployee: state.data.selectedEmployee ?? {},
        },
      };
    case EmployeesActionType.GET_EMPLOYEES_FAILURE:
      return {
        loading: false,
        error: action.payload,
        data: { employees: [], totalEmployees: 0, selectedEmployee: {} },
      };
    case EmployeesActionType.SET_EMPLOYEE:
      return {
        loading: state.loading,
        error: state.error,
        data: {
          ...state.data,
          selectedEmployee: {
            ...state.data.selectedEmployee,
            employee: action.payload.selectedEmployee,
          },
        },
      };
    case EmployeesActionType.GET_EMPLOYEE_DEPENDENTS:
      return {
        loading: true,
        error: null,
        data: {
          ...state.data,
          selectedEmployee: {
            ...state.data.selectedEmployee,
            dependents: [],
            totalDependents: 0,
          },
        },
      };
    case EmployeesActionType.GET_EMPLOYEE_DEPENDENTS_SUCCESS:
      return {
        loading: false,
        error: null,
        data: {
          ...state.data,
          selectedEmployee: {
            ...state.data.selectedEmployee,
            dependents: action.payload.dependents,
            totalDependents: action.payload.totalDependents,
          },
        },
      };
    case EmployeesActionType.GET_EMPLOYEE_DEPENDENTS_FAILURE:
      return {
        loading: false,
        error: action.payload,
        data: {
          ...state.data,
          selectedEmployee: {
            ...state.data.selectedEmployee,
            dependents: [],
            totalDependents: 0,
          },
        },
      };
    case EmployeesActionType.GET_EMPLOYEE_CONTACTS:
      return {
        loading: true,
        error: null,
        data: {
          ...state.data,
          selectedEmployee: {
            ...state.data.selectedEmployee,
            contacts: [],
            totalContacts: 0,
          },
        },
      };
    case EmployeesActionType.GET_EMPLOYEE_CONTACTS_SUCCESS:
      return {
        loading: false,
        error: null,
        data: {
          ...state.data,
          selectedEmployee: {
            ...state.data.selectedEmployee,
            contacts: action.payload.contacts,
            totalContacts: action.payload.totalContacts,
          },
        },
      };
    case EmployeesActionType.GET_EMPLOYEE_CONTACTS_FAILURE:
      return {
        loading: false,
        error: action.payload,
        data: {
          ...state.data,
          selectedEmployee: {
            ...state.data.selectedEmployee,
            contacts: [],
            totalContacts: 0,
          },
        },
      };

    case EmployeesActionType.GET_EMPLOYEE_AUDITABLE:
      return {
        loading: true,
        error: null,
        data: {
          ...state.data,
          selectedEmployee: {
            ...state.data.selectedEmployee,
            auditable: [],
            loadingAuditable: true,
            totalAuditable: 0,
          },
        },
      };
    case EmployeesActionType.GET_EMPLOYEE_AUDITABLE_SUCCESS:
      return {
        loading: false,
        error: null,
        data: {
          ...state.data,
          selectedEmployee: {
            ...state.data.selectedEmployee,
            auditable: action.payload.auditable,
            loadingAuditable: false,
            totalAuditable: action.payload.totalAuditable,
          },
        },
      };
    case EmployeesActionType.GET_EMPLOYEE_AUDITABLE_FAILURE:
      return {
        loading: false,
        error: action.payload,
        data: {
          ...state.data,
          selectedEmployee: {
            ...state.data.selectedEmployee,
            auditable: [],
            loadingAuditable: false,
            totalAuditable: 0,
          },
        },
      };
    case EmployeesActionType.GET_EMPLOYEE_PAY_STATEMENTS:
      return {
        loading: true,
        error: null,
        data: {
          ...state.data,
          selectedEmployee: {
            ...state.data.selectedEmployee,
            payStatements: [],
            totalPayStatements: 0,
          },
        },
      };
    case EmployeesActionType.GET_EMPLOYEE_PAY_STATEMENTS_SUCCESS:
      return {
        loading: false,
        error: null,
        data: {
          ...state.data,
          selectedEmployee: {
            ...state.data.selectedEmployee,
            payStatements: action.payload.payStatements,
            totalPayStatements: action.payload.totalPayStatements,
          },
        },
      };
    case EmployeesActionType.GET_EMPLOYEE_PAY_STATEMENTS_FAILURE:
      return {
        loading: false,
        error: action.payload,
        data: {
          ...state.data,
          selectedEmployee: {
            ...state.data.selectedEmployee,
            payStatements: [],
            totalPayStatements: 0,
          },
        },
      };
    case EmployeesActionType.PATCH_EMPLOYEE_ACCESS:
      return {
        loading: true,
        error: null,
        data: {
          ...state.data,
        },
      };
    case EmployeesActionType.PATCH_EMPLOYEE_ACCESS_SUCCESS: {
      const empIndex = state.data.employees.findIndex((emp: IEmployee) => emp.id === action.payload.employeeId);

      const updatedEmployees = [...state.data.employees];

      const pIndex = updatedEmployees[empIndex].permissions.findIndex(
        (permission: IPermission) => permission.id === action.payload.permissionId
      );

      updatedEmployees[empIndex].permissions[pIndex].isActive =
        !updatedEmployees[empIndex].permissions[pIndex].isActive;

      let updtSelectedEmployee = state.data.selectedEmployee.employee;
      if (updatedEmployees[empIndex].employeeId === state.data.selectedEmployee.employee?.id) {
        updtSelectedEmployee = updatedEmployees[empIndex];
      }
      return {
        loading: false,
        error: null,
        data: {
          ...state.data,
          employees: updatedEmployees,
          selectedEmployee: {
            ...state.data.selectedEmployee,
            employee: updtSelectedEmployee,
          },
        },
      };
    }
    case EmployeesActionType.PATCH_EMPLOYEE_ACCESS_FAILURE:
      return {
        loading: false,
        error: action.payload,
        data: {
          ...state.data,
        },
      };
    case EmployeesActionType.DISCONNECT_EMPLOYEE_ACCESS:
      return {
        loading: true,
        error: null,
        data: {
          ...state.data,
        },
      };
    case EmployeesActionType.DISCONNECT_EMPLOYEE_ACCESS_SUCCESS: {
      const empIndex = state.data.employees.findIndex((emp: IEmployee) => emp.id === action.payload.employeeId);

      const updatedEmployees = [...state.data.employees];

      updatedEmployees[empIndex].accountStatus = 'Inactive';

      let updtSelectedEmployee = state.data.selectedEmployee.employee;

      if (updatedEmployees[empIndex].employeeId === state.data.selectedEmployee.employee?.id) {
        updtSelectedEmployee = updatedEmployees[empIndex];
      }
      return {
        loading: false,
        error: null,
        data: {
          ...state.data,
          employees: updatedEmployees,
          selectedEmployee: {
            ...state.data.selectedEmployee,
            employee: updtSelectedEmployee,
          },
        },
      };
    }
    case EmployeesActionType.DISCONNECT_EMPLOYEE_ACCESS_FAILURE:
      return {
        loading: false,
        error: action.payload,
        data: {
          ...state.data,
        },
      };

    default:
      return state;
  }
};

export default reducer;
